/* =============================================================================
   NORMAX CAPITAL — Canonical navigation taxonomy, breadcrumbs, user & icons.
   Ported from the shared js/normax-crm.js engine. Single source of truth for
   the reusable sidemenu + navbar.
   ============================================================================= */

export interface NavItem {
  key: string;
  label: string;
  /** Router path (also the route data key). */
  path: string;
  /** Backend module name for RBAC sidebar filtering. */
  module?: string;
  /**
   * Present in `NAV` for taxonomy completeness (every route key must appear
   * in both `NAV` and `BREADCRUMBS`) but filtered out of all rendered nav
   * surfaces (`Sidemenu`, `StaticSidemenu`, `StaticIndex`). Two distinct uses:
   *  - account pages reached only via the topbar (Profile, Notifications),
   *    which have no `static-pages/<key>` counterpart to link to;
   *  - de-scoped modules (Indexed Funds, Structured Investments, Direct
   *    Issuances) whose routes/RBAC permissions/static pages still exist —
   *    only the sidebar entry point is removed, not the module itself.
   */
  hidden?: boolean;
}

export interface NavCluster {
  cluster: string;
  items: NavItem[];
}

/** A breadcrumb crumb: label + optional route key (null = current / non-link). */
export type Crumb = [label: string, key: string | null];

export interface CurrentUser {
  name: string;
  initials: string;
  role: string;
  entity: string;
  entityShort: string;
}

export const NAV: NavCluster[] = [
  {
    cluster: 'Operational Core',
    items: [
      { key: 'dashboard',    label: 'Main Dashboard',        path: 'dashboard',    module: 'Dashboard' },
      { key: 'pipeline',     label: 'Sales Pipeline',        path: 'pipeline',     module: 'Sales Pipeline' },
      { key: 'clients',      label: 'Prospects & Investors', path: 'clients',      module: 'Prospects & Investors' },
      { key: 'appointments', label: 'Appointments',          path: 'appointments', module: 'Appointments' },
      { key: 'tasks',        label: 'Follow-ups',            path: 'tasks',        module: 'Follow-ups' },
      { key: 'activities',   label: 'Notes & Interactions',  path: 'activities',   module: 'Notes & Interactions' },
    ],
  },
  {
    cluster: 'Product Information Centre',
    items: [
      { key: 'products',         label: 'Product Hub',            path: 'products',         module: 'Product Hub' },
      { key: 'otc-alternatives', label: 'OTC Alternative Funds',  path: 'otc-alternatives', module: 'OTC Alternative Funds' },
      { key: 'indexed-funds',    label: 'Indexed Funds',          path: 'indexed-funds',    module: 'Indexed Funds',    hidden: true },
      { key: 'structured',       label: 'Structured Investments', path: 'structured',       module: 'Structured Investments', hidden: true },
      { key: 'direct-issuances', label: 'Direct Issuances',       path: 'direct-issuances', module: 'Direct Issuances', hidden: true },
      { key: 'rwa',              label: 'RWA Tokenisation',       path: 'rwa',              module: 'RWA Tokenisation' },
      { key: 'trust-banking',    label: 'Trust Banking',          path: 'trust-banking',    module: 'Trust Banking' },
    ],
  },
  {
    cluster: 'Fiduciary Services',
    items: [
      { key: 'wealth-management', label: 'Wealth Management', path: 'wealth-management', module: 'Wealth Management' },
      { key: 'trade-finance',     label: 'Trade Finance',     path: 'trade-finance',     module: 'Trade Finance' },
      { key: 'escrow',            label: 'Escrow Services',   path: 'escrow',            module: 'Escrow Services' },
      { key: 'trust',             label: 'Trust Services',    path: 'trust',             module: 'Trust Services' },
    ],
  },
  {
    cluster: 'Compliance & Onboarding',
    items: [
      { key: 'suitability', label: 'Suitability Tracking', path: 'suitability', module: 'Suitability Tracking' },
      { key: 'kyc-aml',     label: 'KYC & AML Status',     path: 'kyc-aml',     module: 'KYC & AML Status' },
      { key: 'documents',   label: 'Documentation',         path: 'documents',   module: 'Documentation' },
      { key: 'compliance',  label: 'Compliance Alerts',     path: 'compliance',  module: 'Compliance Alerts' },
    ],
  },
  {
    cluster: 'Sales Enablement',
    items: [
      { key: 'scripts',    label: 'Sales Scripts',      path: 'scripts',    module: 'Sales Scripts' },
      { key: 'objections', label: 'Objection Handling',  path: 'objections', module: 'Objection Handling' },
    ],
  },
  {
    cluster: 'Reporting & Management',
    items: [
      { key: 'reporting',  label: 'Reporting Dashboards', path: 'reporting',  module: 'Reporting Dashboards' },
      { key: 'management', label: 'Management Review',    path: 'management', module: 'Management Review' },
    ],
  },
  {
    cluster: 'Administration',
    items: [
      { key: 'users', label: 'Users', path: 'users', module: 'Users' },
      { key: 'roles', label: 'Roles', path: 'roles', module: 'Roles' },
    ],
  },
  {
    // Reached only via the topbar (user-chip dropdown / bell "View all") —
    // see the `hidden` doc on NavItem for why these aren't in the sidebar.
    cluster: 'Account',
    items: [
      { key: 'profile', label: 'My Profile', path: 'profile', hidden: true },
      { key: 'notifications', label: 'Notifications', path: 'notifications', hidden: true },
    ],
  },
];

export const BREADCRUMBS: Record<string, Crumb[]> = {
  'dashboard':         [['Home', 'dashboard']],
  'pipeline':          [['Home', 'dashboard'], ['Sales Pipeline', null]],
  'clients':           [['Home', 'dashboard'], ['Prospects & Investors', null]],
  'appointments':      [['Home', 'dashboard'], ['Appointments', null]],
  'tasks':             [['Home', 'dashboard'], ['Follow-ups', null]],
  'activities':        [['Home', 'dashboard'], ['Notes & Interactions', null]],
  'products':          [['Home', 'dashboard'], ['Product Information Centre', null]],
  'otc-alternatives':  [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['OTC Alternative Funds', null]],
  'indexed-funds':     [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['Indexed Funds', null]],
  'structured':        [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['Structured Investments', null]],
  'direct-issuances':  [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['Direct Issuances', null]],
  'rwa':               [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['RWA Tokenisation', null]],
  'trust-banking':     [['Home', 'dashboard'], ['Product Information Centre', 'products'], ['Trust Banking', null]],
  'wealth-management': [['Home', 'dashboard'], ['Fiduciary Services', null], ['Wealth Management', null]],
  'trade-finance':     [['Home', 'dashboard'], ['Fiduciary Services', null], ['Trade Finance', null]],
  'escrow':            [['Home', 'dashboard'], ['Fiduciary Services', null], ['Escrow Services', null]],
  'trust':             [['Home', 'dashboard'], ['Fiduciary Services', null], ['Trust Services', null]],
  'suitability':       [['Home', 'dashboard'], ['Compliance & Onboarding', null], ['Suitability Tracking', null]],
  'kyc-aml':           [['Home', 'dashboard'], ['Compliance & Onboarding', null], ['KYC & AML', null]],
  'documents':         [['Home', 'dashboard'], ['Compliance & Onboarding', null], ['Documentation', null]],
  'compliance':        [['Home', 'dashboard'], ['Compliance & Onboarding', null], ['Compliance Alerts', null]],
  'scripts':           [['Home', 'dashboard'], ['Sales Enablement', null], ['Sales Scripts', null]],
  'objections':        [['Home', 'dashboard'], ['Sales Enablement', null], ['Objection Handling', null]],
  'reporting':         [['Home', 'dashboard'], ['Reporting & Management', null], ['Reporting Dashboards', null]],
  'management':        [['Home', 'dashboard'], ['Reporting & Management', null], ['Management Review', null]],
  'admin':             [['Home', 'dashboard'], ['Administrator Settings', null]],
  'users':             [['Home', 'dashboard'], ['Users', null]],
  'roles':             [['Home', 'dashboard'], ['Roles', null]],
  'profile':           [['Home', 'dashboard'], ['My Profile', null]],
  'notifications':     [['Home', 'dashboard'], ['Notifications', null]],
};

export const CURRENT_USER: CurrentUser = {
  name: 'José Carlos Quian',
  initials: 'JQ',
  role: 'Chairman',
  entity: 'NORMAX CAPITAL K.B.',
  entityShort: 'STOCKHOLM',
};

const DEFAULT_ICON =
  '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="7" r="5"/></svg>';

export const ICONS: Record<string, string> = {
  'dashboard':         '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="1" width="5" height="5" rx="0.5"/><rect x="8" y="1" width="5" height="5" rx="0.5"/><rect x="1" y="8" width="5" height="5" rx="0.5"/><rect x="8" y="8" width="5" height="5" rx="0.5"/></svg>',
  'pipeline':          '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="2" width="3" height="10"/><rect x="5.5" y="2" width="3" height="6"/><rect x="10" y="2" width="3" height="8"/></svg>',
  'clients':           '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="5" r="2.5"/><path d="M2 13c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5"/></svg>',
  'appointments':      '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1.5" y="3" width="11" height="9.5" rx="1"/><path d="M1.5 6h11"/><path d="M4.5 1.5v3M9.5 1.5v3"/></svg>',
  'tasks':             '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4l1.5 1.5L6 3"/><path d="M2 9l1.5 1.5L6 8"/><path d="M8 4h5M8 9h5"/></svg>',
  'activities':        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4h10M2 7h10M2 10h6"/></svg>',
  'products':          '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1l5.5 3v6L7 13 1.5 10V4z"/><path d="M1.5 4L7 7l5.5-3M7 7v6"/></svg>',
  'otc-alternatives':  '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="7" r="5.5"/><path d="M7 1.5v11M1.5 7h11"/></svg>',
  'indexed-funds':     '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1.5 11l3-3 2.5 2 5-5"/><path d="M9 5h3v3"/></svg>',
  'structured':        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1L1.5 4v6L7 13l5.5-3V4z"/></svg>',
  'direct-issuances':  '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="2" width="10" height="10" rx="0.5"/><path d="M4 5h6M4 7.5h6M4 10h4"/></svg>',
  'rwa':               '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><polygon points="7,1.5 12,4.5 12,9.5 7,12.5 2,9.5 2,4.5"/></svg>',
  'wealth-management': '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1.5v11M3.5 4.5c0-1 1.5-2 3.5-2s3.5 1 3.5 2-1.5 2-3.5 2-3.5 1-3.5 2 1.5 2 3.5 2 3.5-1 3.5-2"/></svg>',
  'trade-finance':     '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M1.5 11h11M2 11V5l5-3 5 3v6"/><rect x="6" y="7" width="2" height="4"/></svg>',
  'escrow':            '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="6" width="10" height="7" rx="0.5"/><path d="M4 6V4a3 3 0 016 0v2"/></svg>',
  'trust':             '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1l5 2v4.5c0 3-2.5 5-5 5.5-2.5-.5-5-2.5-5-5.5V3z"/></svg>',
  'trust-banking':     '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 5l5-3 5 3M3 5v6M11 5v6M5 5v6M9 5v6M7 5v6M2 12h10"/></svg>',
  'suitability':       '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4l1.5 1.5L6 3M2 8.5l1.5 1.5L6 7.5M8 4h5M8 9h5"/></svg>',
  'kyc-aml':           '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="6" cy="5.5" r="2.5"/><path d="M1.5 12c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"/><circle cx="11" cy="11" r="2"/></svg>',
  'documents':         '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 1.5h6l3 3v8a0.5 0.5 0 01-0.5 0.5H3a0.5 0.5 0 01-0.5-0.5v-10A0.5 0.5 0 013 1.5z"/><path d="M9 1.5v3h3"/></svg>',
  'compliance':        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1l5 2v4.5c0 3-2.5 5-5 5.5-2.5-.5-5-2.5-5-5.5V3z"/><path d="M5 7l1.5 1.5L9.5 5.5"/></svg>',
  'scripts':           '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 2.5h10v9H4l-2 2z"/><path d="M4.5 5.5h5M4.5 8h3"/></svg>',
  'objections':        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="7" r="5.5"/><path d="M7 4v3.5M7 10v0.5"/></svg>',
  'reporting':         '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 12h10M3 12V7M6 12V4M9 12V8M12 12V6"/></svg>',
  'management':        '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="4.5" cy="5" r="2"/><circle cx="9.5" cy="5" r="2"/><path d="M1 12c0-2 1.5-3.5 3.5-3.5s3.5 1.5 3.5 3.5M6 12c0-2 1.5-3.5 3.5-3.5s3.5 1.5 3.5 3.5"/></svg>',
  'admin':             '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="7" r="2"/><path d="M7 1v1.5M7 11.5V13M1 7h1.5M11.5 7H13M2.6 2.6l1 1M10.4 10.4l1 1M2.6 11.4l1-1M10.4 3.6l1-1"/></svg>',
  'users':             '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="5" cy="4.5" r="2"/><path d="M1 12c0-2.2 1.8-4 4-4s4 1.8 4 4"/><circle cx="10.5" cy="5" r="1.5"/><path d="M10.5 8.5c1.7 0 3 1.2 3 2.5"/></svg>',
  'roles':             '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 1l4.5 2v3.5c0 2.8-2 4.8-4.5 5.5-2.5-.7-4.5-2.7-4.5-5.5V3z"/><circle cx="7" cy="6" r="1.5"/><path d="M4.5 10c0-1.4 1.1-2.2 2.5-2.2s2.5.8 2.5 2.2"/></svg>',
  // Both `hidden` in NAV (topbar-only, no sidebar entry) — icons kept for
  // taxonomy completeness. Paths match their existing topbar usage exactly
  // (navbar.html's Profile dropdown link / notifications bell).
  'profile':           '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="7" cy="5" r="2.5"/><path d="M1.5 12.5c0-2.76 2.46-5 5.5-5s5.5 2.24 5.5 5"/></svg>',
  'notifications':     '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 5.5a4 4 0 018 0V8l1 2.5H2L3 8z"/><path d="M5.5 11.5a1.5 1.5 0 003 0"/></svg>',
};

export function iconFor(key: string): string {
  return ICONS[key] ?? DEFAULT_ICON;
}
